import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { useEnabledQuery } from '../../../../ui/hooks/useEnabledQuery';
import { getMutationSchema } from '../../../../ui/util/getMutationSchema';
import { ResourceType } from '../../../enumerations/ResourceType';
import { get } from '../../../resource-base/APIVersion1';
import { useIndirectlyVersionedResourceAdapter } from '../../../resource-base/adapters/useIndirectlyVersionedResourceAdapter';
import { useMultiResourceSingleParentedResourceAdapterActions } from '../../../resource-base/adapters/useMultiResourceSingleParentedResourceAdapterActions';
import {
    defaultIndirectlyVersionedAndNamedArtifactResource,
    IndirectlyVersionedAndNamedArtifactResourceSchema,
} from '../../../resource-base/artifactResourceTypes';
import { IdSchema } from '../../../resource-base/baseSchemas';

export enum RolePropertyValidation {
    NAME = 'NAME',
    DESCRIPTION = 'DESCRIPTION',
    AUTHORITIES = 'AUTHORITIES',
    TRAININGS = 'TRAININGS',
    TASKS = 'TASKS',
    COLOR = 'COLOR',
}

export const RoleResourceSchema = IndirectlyVersionedAndNamedArtifactResourceSchema.extend({
    resourceType: z.literal(ResourceType.ROLE).default(ResourceType.ROLE),
    description: z.string(),
    tasks: z.string().array(),
    authorities: z.string().array(),
    trainings: z.string().array(),
    color: z.string(),
});
export const RoleMutationResourceSchema = getMutationSchema(RoleResourceSchema);

export type RoleResource = z.infer<typeof RoleResourceSchema>;

export const defaultRoleResource: RoleResource = {
    ...defaultIndirectlyVersionedAndNamedArtifactResource,
    resourceType: ResourceType.ROLE,
    description: '',
    color: '',
    tasks: [],
    authorities: [],
    trainings: [],
};

export function useRoleActions() {
    const getEnabled = useEnabledQuery();
    const actions = {
        ...useMultiResourceSingleParentedResourceAdapterActions('project/role', ResourceType.ROLE, RoleResourceSchema),
        ...useIndirectlyVersionedResourceAdapter('project/role', ResourceType.ROLE, RoleResourceSchema),
    };

    const useGetAllRoleIdsInProjectUsedInWorkflowRepresentation = (
        projectVersionId: string | undefined | null,
        workflowRepresentationId: string | undefined | null
    ) => {
        return useQuery({
            queryKey: [projectVersionId, workflowRepresentationId, ResourceType.ROLE],
            queryFn: () =>
                get(`project/role/used-in/${workflowRepresentationId}/versioned-in/${projectVersionId}`).then(
                    (result) => actions.processGetResponse(result, [] as string[], IdSchema.array())
                ),
            enabled: getEnabled(projectVersionId && workflowRepresentationId),
        });
    };

    return {
        ...actions,
        useGetAllRoleIdsInProjectUsedInWorkflowRepresentation,
    };
}
